import { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import Seo from '@/components/Seo';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { ToolLongForm } from '@/components/tools/ToolLongForm';
import { ToolFAQ } from '@/components/tools/ToolFAQ';
import { AdSlot } from '@/components/ads/AdSlot';
import { downloadBlob, dataUrlToBlob } from '@/lib/download';
import { QrCode, Download, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const FAQS = [
    { q: 'What can I encode?', a: 'Any text or URL up to a few thousand characters. Longer content forces a denser QR that’s harder for phones to scan.' },
    { q: 'What is error correction level?', a: 'It’s the amount of redundancy baked into the QR. “Low” is smallest and easiest to scan; “High” is bigger but still readable when up to 30% of the code is damaged, dirty, or covered.' },
    { q: 'Does the tool send my link anywhere?', a: 'No. The QR is generated entirely in your browser — the string you type never leaves your device.' },
    { q: 'Can I download the QR as SVG?', a: 'Yes. Choose SVG in the format selector for a lossless, vector-scalable download.' },
    { q: 'How large can I print it?', a: 'As large as you like — QR codes are vector-friendly. A rule of thumb: the code should be scannable from at least 10× its own width.' },
];

const HOW = [
    'Type or paste anything you want to encode — a URL, a Wi-Fi password, a business card in vCard format, or a short block of plain text. The QR image regenerates as you type, using the qrcode library entirely inside your browser.',
    'You can control the error correction level and the size of the output. Higher error correction means the code stays scannable even if parts of it are covered by a logo or damaged. Larger sizes produce cleaner prints and easier scans from further away.',
    'When you’re happy with the result, download it as a PNG (great for the web and quick sharing) or as an SVG (perfect for print, signage, and any placement that might get resized). Both formats are generated locally.',
    'Because everything happens in your browser, nothing about the content you encode is uploaded, logged, or stored anywhere. Bookmark the page for offline access after the first load.',
];
const BENEFITS = [
    'QR codes are the fastest way to bridge the physical and digital world. A well-placed code on a menu, a flyer, a business card, or a piece of packaging can save a customer a full minute of typing — and in that minute you might have lost them. Instead of “visit our site at www.longdomain.com/campaign/2025” you can just show them a square they scan and go.',
    'For events, workshops, and conferences, QR codes make check-in and content sharing frictionless. Encode a link to a shared drive, a signup form, or a slack invite, and let people jump in without asking for the URL to be spelled out.',
    'For personal use, QR codes are handy for sharing a Wi-Fi password with houseguests, publishing a URL to your resume, or connecting phones to your calendar or contact card. Small quality-of-life wins that compound over time.',
];
const TIPS = [
    'Use error correction level “High” if you plan to overlay a small logo on the QR.',
    'Keep at least a 10 %–of-code-width white margin around the QR — without it, cameras struggle.',
    'Test print by scanning from the intended distance before ordering hundreds.',
];

export default function QrGeneratorPage() {
    const [text, setText] = useState('https://utilitytool.co.za');
    const [ec, setEc] = useState('M'); // L, M, Q, H
    const [size, setSize] = useState('512');
    const [dataUrl, setDataUrl] = useState('');
    const canvasRef = useRef(null);
    const [svgMarkup, setSvgMarkup] = useState('');

    const opts = useMemo(
        () => ({
            errorCorrectionLevel: ec,
            margin: 2,
            color: { dark: '#0f172a', light: '#ffffff' },
            width: Math.max(128, Math.min(2048, Number(size) || 512)),
        }),
        [ec, size],
    );

    useEffect(() => {
        let cancelled = false;
        if (!text) {
            setDataUrl('');
            setSvgMarkup('');
            return () => {};
        }
        QRCode.toDataURL(text, opts)
            .then((url) => {
                if (!cancelled) setDataUrl(url);
            })
            .catch(() => {
                if (!cancelled) setDataUrl('');
            });
        QRCode.toString(text, { ...opts, type: 'svg' })
            .then((svg) => {
                if (!cancelled) setSvgMarkup(svg);
            })
            .catch(() => {
                if (!cancelled) setSvgMarkup('');
            });
        return () => {
            cancelled = true;
        };
    }, [text, opts]);

    const downloadPng = () => {
        if (!dataUrl) return;
        // Convert the QRCode-generated data URL into a Blob so it flows
        // through the same helper (and iOS Safari fallback) as every
        // other client-side download in the app.
        const blob = dataUrlToBlob(dataUrl);
        if (!blob) {
            toast.error('Could not prepare the PNG for download.');
            return;
        }
        downloadBlob(blob, 'qr-code.png');
    };
    const downloadSvg = () => {
        if (!svgMarkup) return;
        const blob = new Blob([svgMarkup], { type: 'image/svg+xml' });
        downloadBlob(blob, 'qr-code.svg');
    };

    const structured = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'UtilityTool — QR Code Generator',
        applicationCategory: 'Utility',
        operatingSystem: 'Any',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    };

    return (
        <>
            <Seo
                title="QR Code Generator"
                description="Turn any link or text into a scannable QR code. Download as PNG or SVG. Free, private, runs in your browser."
                canonical="/tools/qr-generator"
                structuredData={structured}
            />
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14">
                <Breadcrumb items={[{ label: 'Tools', to: '/tools' }, { label: 'QR Code Generator' }]} />
                <div className="mt-4 flex items-start gap-4">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--primary))]">
                        <QrCode className="w-6 h-6" />
                    </span>
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">QR Code Generator</h1>
                        <p className="mt-2 text-slate-600 max-w-2xl">
                            Turn any link, phrase, or text block into a scannable QR code.
                            Download as PNG or SVG.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">In-browser</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">PNG + SVG</Badge>
                        </div>
                    </div>
                </div>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-6 lg:gap-8">
                    <Card className="p-5 sm:p-6 border-border/80">
                        <Label htmlFor="qr-input">Content</Label>
                        <Textarea
                            id="qr-input"
                            data-testid="tool-qr-input"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={6}
                            placeholder="URL, Wi-Fi string, plain text…"
                            className="mt-1.5 leading-relaxed"
                        />
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label>Error correction</Label>
                                <Select value={ec} onValueChange={setEc}>
                                    <SelectTrigger data-testid="tool-qr-ec" className="mt-1.5">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="L">Low (7%)</SelectItem>
                                        <SelectItem value="M">Medium (15%)</SelectItem>
                                        <SelectItem value="Q">Quartile (25%)</SelectItem>
                                        <SelectItem value="H">High (30%)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="qr-size">Size (px)</Label>
                                <Input
                                    id="qr-size"
                                    data-testid="tool-qr-size"
                                    type="number"
                                    min={128}
                                    max={2048}
                                    value={size}
                                    onChange={(e) => setSize(e.target.value)}
                                    className="mt-1.5"
                                />
                            </div>
                        </div>
                    </Card>

                    <Card className="p-5 sm:p-6 border-border/80 flex flex-col items-center justify-center">
                        {dataUrl ? (
                            <img
                                src={dataUrl}
                                alt="QR code"
                                data-testid="tool-qr-preview"
                                className="w-full max-w-[320px] aspect-square rounded-lg border border-border/70 bg-white p-3"
                                width={320}
                                height={320}
                            />
                        ) : (
                            <div className="w-full max-w-[320px] aspect-square rounded-lg border border-dashed border-border grid place-content-center text-sm text-slate-500">
                                Enter text to generate
                            </div>
                        )}
                        <div className="mt-4 flex gap-2">
                            <Button
                                onClick={downloadPng}
                                disabled={!dataUrl}
                                data-testid="tool-qr-download-png"
                                className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--link-hover))] text-white"
                            >
                                <Download className="w-4 h-4 mr-2" /> PNG
                            </Button>
                            <Button
                                variant="outline"
                                onClick={downloadSvg}
                                disabled={!svgMarkup}
                                data-testid="tool-qr-download-svg"
                            >
                                <Download className="w-4 h-4 mr-2" /> SVG
                            </Button>
                        </div>
                    </Card>
                </div>

                <div className="mt-6 rounded-xl border border-border/70 bg-[hsl(var(--surface-2))] p-4">
                    <div className="flex items-center gap-2 text-slate-800">
                        <ShieldCheck className="w-4 h-4 text-[hsl(var(--primary))]" />
                        <span className="text-sm font-semibold">Generated in your browser</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">
                        The content you encode never leaves your device.
                    </p>
                </div>

                <ToolLongForm howItWorks={HOW} benefits={BENEFITS} tips={TIPS} />
                <AdSlot placement="tool" />
                <ToolFAQ items={FAQS} />
            </section>
        </>
    );
}
